import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faPlus, faDollarSign, faSearch } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import Loading from '../pages/Loading'; // Assuming you have a Loading component
import {toast, Toaster} from "sonner"; 

const apiURL = import.meta.env.VITE_API_URL;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUserEmail, setEditUserEmail] = useState(null); // Use email instead of ID
  const [editFormData, setEditFormData] = useState({
    firstname: '',
    lastname: '',
    balance: '',
    daily_profit: '',
    weekly_profit: '',
    deposit: '',
    emailaddress: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false); // Modal state
  const [actionLoading, setActionLoading] = useState(false); // New state for tracking delete/edit loading

  const filteredUsers = users.filter(user =>
    user.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${apiURL}/admin/users`);
        setUsers(response.data);
      } catch (error) {
        setErrorMessage('Error fetching users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleAddUserClick = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setEditUserEmail(null); // Reset edit email on close
    setEditFormData({
      firstname: '',
      lastname: '',
      balance: '',
      daily_profit: '',
      weekly_profit: '',
      deposit: '',
      emailaddress: '',
    });
  };

  const handleEditClick = (user) => {
    setEditUserEmail(user.emailaddress); // Set the email of the user to edit
    setEditFormData({ ...user }); // Fill the form with the user’s current data
    setShowModal(true); // Show modal when editing
  };

  const handleSaveClick = async () => {
    if (!editFormData.firstname || !editFormData.emailaddress) {
      setErrorMessage('Please fill in all required fields.');
      return;
    }

    setActionLoading(true); // Show loading spinner when saving
    try {
      const updatedUser = { ...editFormData };
      await axios.post(`${apiURL}/admin/update`, { emailaddress: editUserEmail, ...updatedUser });
      setUsers(users.map((user) => (user.emailaddress === editUserEmail ? { ...user, ...updatedUser } : user)));
      handleCloseModal(); // Close the modal after saving
      toast.success('User updated successfully'); // Success notification
    } catch (error) {
      setErrorMessage('Error updating user. Please try again.');
      toast.error('Error updating user');
    } finally {
      setActionLoading(false); // Stop loading spinner
    }
  };

  const handleDeleteUser = async (emailaddress) => {
    setActionLoading(true); // Show loading spinner when deleting
    try {
      await axios.delete(`${apiURL}/admin/delete/${emailaddress}`);
      setUsers(users.filter(user => user.emailaddress !== emailaddress));
      toast.success('User deleted successfully'); // Success notification
    } catch (error) {
      setErrorMessage('Error deleting user. Please try again.');
      toast.error('Error deleting user');
    } finally {
      setActionLoading(false); // Stop loading spinner
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({ ...editFormData, [name]: value });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="container mx-auto p-6 ">
		<Toaster position = "top-center" richColors/>
      <div>
        <h1 className="text-4xl font-bold text-center mb-8">Admin Dashboard</h1>
        {errorMessage && <p className="text-red-500 text-center">{errorMessage}</p>}

        <div className="flex justify-between mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FontAwesomeIcon icon={faSearch} className="absolute top-2 right-2 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 w-[80vw]">
        {filteredUsers.map((user) => (
          <motion.div
            key={user.emailaddress}
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-transform transform hover:scale-105"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-xl font-semibold">{user.firstname} {user.lastname}</h2>
            <p><strong>Balance:</strong> <FontAwesomeIcon icon={faDollarSign} className="mr-1" />{user.balance}</p>
            <p><strong>Daily Profit:</strong> <FontAwesomeIcon icon={faDollarSign} className="mr-1" />{user.daily_profit}</p>
            <p><strong>Weekly Profit:</strong> <FontAwesomeIcon icon={faDollarSign} className="mr-1" />{user.weekly_profit}</p>
            <p><strong>Deposit:</strong> <FontAwesomeIcon icon={faDollarSign} className="mr-1" />{user.deposit}</p>
            <p><strong>Email:</strong> {user.emailaddress}</p>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => handleEditClick(user)} // Ensure this triggers correctly
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300"
              >
                {actionLoading ? 'Loading...' : <><FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit</>}
              </button>
              <button
                onClick={() => handleDeleteUser(user.emailaddress)}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300"
              >
                {actionLoading ? 'Deleting...' : <><FontAwesomeIcon icon={faTrashAlt} className="mr-1" /> Delete</>}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Edit Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex justify-center items-center">
          <motion.div
            className="bg-white p-6 rounded-lg shadow-lg w-80 md:w-96"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-2xl font-bold mb-4">{editUserEmail ? 'Editing User' : 'Add New User'}</h2>
            <form>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Balance:</label>
                <input
                  type="text"
                  name="balance"
                  value={editFormData.balance}
                  onChange={handleEditChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Daily Profit:</label>
                <input
                  type="text"
                  name="daily_profit"
                  value={editFormData.daily_profit}
                  onChange={handleEditChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Weekly Profit:</label>
                <input
                  type="text"
                  name="weekly_profit"
                  value={editFormData.weekly_profit}
                  onChange={handleEditChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-bold">Deposit:</label>
                <input
                  type="text"
                  name="deposit"
                  value={editFormData.deposit}
                  onChange={handleEditChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={handleSaveClick}
                  className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg"
                  disabled={actionLoading} // Disable while loading
                >
                  {actionLoading ? 'Saving...' : <><FontAwesomeIcon icon={faDollarSign} className="mr-1" /> Save</>}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Users;

